import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { debounce, mediaQueryLarge, startViewTransition } from '@theme/utilities';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
import { CollectionUpdateEvent, SearchUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /** @type {{ filters: string, statuses: { element: Element, html: string, className: string }[], prices: { element: Element, style: string | null }[], bounds: { element: Element, min: string | null, max: string | null }[] } | null} */
  #drawerSnapshot = null;
  #appliedDrawerFilters = false;
  #isApplyingDrawerFilters = false;

  connectedCallback() {
    super.connectedCallback();
    // In vertical filter mode the in-page filters take over at ≥750px, so a
    // drawer left open across a mobile→desktop resize would render duplicate
    // filter UI. `data-filter-style` is only set on the drawer variant (see
    // blocks/filters.liquid), so matching "vertical" here uniquely targets it.
    if (this.dataset.filterStyle === 'vertical') {
      mediaQueryLarge.addEventListener('change', this.#onBreakpointChange);
    }
    if (this.dataset.pageType === 'collection' && this.closest('theme-drawer')) {
      const drawer = this.closest('theme-drawer');
      drawer?.addEventListener('theme-drawer:open', this.#onDrawerOpen);
      drawer?.addEventListener('theme-drawer:close', this.#onDrawerClose);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#onBreakpointChange);
    const drawer = this.closest('theme-drawer');
    drawer?.removeEventListener('theme-drawer:open', this.#onDrawerOpen);
    drawer?.removeEventListener('theme-drawer:close', this.#onDrawerClose);
  }

  #filterState() {
    const entries = [...this.createURLParameters().entries()].filter(([name]) => name.startsWith('filter.'));
    entries.sort(([nameA, valueA], [nameB, valueB]) => nameA.localeCompare(nameB) || valueA.localeCompare(valueB));
    return JSON.stringify(entries);
  }

  #onDrawerOpen = () => {
    const form = this.refs.facetsForm;
    if (!(form instanceof HTMLFormElement)) return;

    form.querySelectorAll('details').forEach((details) => {
      details.open = false;
    });
    this.#drawerSnapshot = {
      filters: this.#filterState(),
      statuses: [...form.querySelectorAll('facet-status-component [ref="facetStatus"]')].map((element) => ({
        element,
        html: element.innerHTML,
        className: element.className,
      })),
      prices: [...form.querySelectorAll('price-facet-component')].map((element) => ({
        element,
        style: element.getAttribute('style'),
      })),
      bounds: [...form.querySelectorAll('price-facet-component input[data-min][data-max]')].map((element) => ({
        element,
        min: element.getAttribute('data-min'),
        max: element.getAttribute('data-max'),
      })),
    };
    const error = this.closest('theme-drawer')?.querySelector('.insane-filters-drawer__error');
    if (error instanceof HTMLElement) error.hidden = true;
    this.updateApplyButton();
  };

  #onDrawerClose = () => {
    const form = this.refs.facetsForm;
    const snapshot = this.#drawerSnapshot;
    this.#drawerSnapshot = null;
    form?.querySelectorAll('details').forEach((details) => {
      details.open = false;
    });

    if (this.#appliedDrawerFilters) {
      this.#appliedDrawerFilters = false;
      return;
    }
    if (!(form instanceof HTMLFormElement) || !snapshot) return;

    form.reset();
    snapshot.statuses.forEach(({ element, html, className }) => {
      element.innerHTML = html;
      element.className = className;
    });
    snapshot.prices.forEach(({ element, style }) => {
      if (style === null) element.removeAttribute('style');
      else element.setAttribute('style', style);
    });
    snapshot.bounds.forEach(({ element, min, max }) => {
      if (min === null) element.removeAttribute('data-min');
      else element.setAttribute('data-min', min);
      if (max === null) element.removeAttribute('data-max');
      else element.setAttribute('data-max', max);
    });
    const error = this.closest('theme-drawer')?.querySelector('.insane-filters-drawer__error');
    if (error instanceof HTMLElement) error.hidden = true;
    this.updateApplyButton();
  };

  #onBreakpointChange = (/** @type {MediaQueryListEvent} */ event) => {
    if (!event.matches) return;
    const drawer = document.getElementById('filters-drawer');
    if (drawer && 'close' in drawer && typeof drawer.close === 'function') {
      drawer.close();
    }
  };

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    const defaultPriceInputs = this.refs.facetsForm.querySelectorAll(
      'input[type="text"][name^="filter.v.price."][data-default-value]'
    );
    defaultPriceInputs.forEach((input) => {
      if (input instanceof HTMLInputElement && input.value === input.dataset.defaultValue) {
        newParameters.delete(input.name);
      }
    });

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    const currentURL = new URL(window.location.href);
    const hasFilterControls = this.refs.facetsForm.querySelector('[name^="filter."]') !== null;
    if (!hasFilterControls) {
      for (const [key, value] of currentURL.searchParams.entries()) {
        if (key.startsWith('filter.')) newParameters.append(key, value);
      }
    }

    const currentSort = currentURL.searchParams.get('sort_by');
    if (currentSort && !newParameters.has('sort_by')) newParameters.set('sort_by', currentSort);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    this.#updateURLHash();
    const renderPromise = this.#updateSection();
    if (this.dataset.pageType === 'search') {
      this.#dispatchSearchUpdateEvent(renderPromise);
    } else {
      this.#dispatchCollectionUpdateEvent(renderPromise);
    }

    return renderPromise;
  };

  /**
   * Applies the staged drawer values, then closes the drawer.
   */
  applyFilters = async () => {
    const drawer = this.closest('theme-drawer');
    if (this.dataset.pageType !== 'collection') {
      const renderPromise = this.updateFilters();
      if (drawer && 'close' in drawer && typeof drawer.close === 'function') drawer.close();
      return renderPromise;
    }
    if (this.#isApplyingDrawerFilters) return;

    const previousURL = window.location.href;
    const previousState = history.state;
    const error = drawer?.querySelector('.insane-filters-drawer__error');
    if (error instanceof HTMLElement) error.hidden = true;
    const applyButton = drawer?.querySelector('.insane-filters-drawer__apply');
    if (applyButton instanceof HTMLButtonElement) applyButton.disabled = true;
    this.#isApplyingDrawerFilters = true;

    try {
      await this.updateFilters();
      this.#appliedDrawerFilters = true;
      if (drawer && 'close' in drawer && typeof drawer.close === 'function') drawer.close();
    } catch {
      history.replaceState(previousState, '', previousURL);
      if (error instanceof HTMLElement) error.hidden = false;
    } finally {
      this.#isApplyingDrawerFilters = false;
      this.updateApplyButton();
    }
  };

  /**
   * Clears staged and applied drawer filters while preserving the active sort.
   */
  clearFilters = () => {
    const form = this.refs.facetsForm;
    if (!(form instanceof HTMLFormElement)) return;

    form.querySelectorAll('input[type="checkbox"][name^="filter."]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.checked = false;
    });
    form.querySelectorAll('input[type="text"][name^="filter."]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.value = '';
    });
    form.querySelectorAll('price-facet-component').forEach((priceFacet) => {
      priceFacet.querySelectorAll('input[type="text"][data-default-value]').forEach((input) => {
        if (input instanceof HTMLInputElement) input.value = input.dataset.defaultValue ?? '';
      });
      const rangeMin = priceFacet.querySelector('[ref="rangeMin"]');
      const rangeMax = priceFacet.querySelector('[ref="rangeMax"]');
      if (rangeMin instanceof HTMLInputElement) rangeMin.value = rangeMin.min;
      if (rangeMax instanceof HTMLInputElement) rangeMax.value = rangeMax.max;
      if (priceFacet instanceof HTMLElement) {
        priceFacet.style.setProperty('--price-range-min-position', '0%');
        priceFacet.style.setProperty('--price-range-max-position', '100%');
      }
    });
    form.querySelectorAll('facet-status-component [ref="facetStatus"]').forEach((status) => {
      status.textContent = '';
    });

    this.updateApplyButton();
  };

  /**
   * Keeps the drawer Apply label and disabled state in sync with staged inputs.
   */
  updateApplyButton() {
    const form = this.refs.facetsForm;
    const dialog = this.closest('dialog');
    const applyButton = dialog?.querySelector('.insane-filters-drawer__apply');
    const clearButton = dialog?.querySelector('.facets__clear-all');
    if (!(form instanceof HTMLFormElement) || !(applyButton instanceof HTMLButtonElement)) return;

    if (this.dataset.pageType !== 'collection') {
      applyButton.disabled = false;
      return;
    }

    const stagedFilters = this.#filterState();
    applyButton.disabled = this.#isApplyingDrawerFilters || !this.#drawerSnapshot || stagedFilters === this.#drawerSnapshot.filters;
    if (clearButton instanceof HTMLButtonElement) clearButton.disabled = stagedFilters === '[]';
  }

  /**
   * Updates the section
   * @returns {Promise<void>}
   */
  #updateSection() {
    const viewTransition = !this.closest('dialog');

    if (viewTransition) {
      return startViewTransition(() => sectionRenderer.renderSection(this.sectionId), ['product-grid']);
    } else {
      return sectionRenderer.renderSection(this.sectionId).then(() => {});
    }
  }

  /**
   * Dispatches search:update event if on the search page.
   * @param {Promise<void>} renderPromise - The promise from section rendering
   */
  #dispatchSearchUpdateEvent(renderPromise) {
    const query = this.#getSearchQuery();

    const deferredPromise = SearchUpdateEvent.createPromise();
    const productFilters = SearchUpdateEvent.parseProductFilters();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query,
          ...(productFilters && { productFilters }),
          sortKey: SearchUpdateEvent.getSortKey(),
        },
        promise: deferredPromise.promise,
      })
    );

    // Resolve promise after section renders with result count
    renderPromise
      .then(() => deferredPromise.resolve({ totalCount: this.#getResultsCount() }))
      .catch((error) => deferredPromise.reject(error));
  }

  /**
   * Gets the count of product results from the server-rendered data attribute.
   * @returns {number}
   */
  #getResultsCount() {
    return Number(this.dataset.resultsCount) || 0;
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    const renderPromise = this.#updateSection();
    if (this.dataset.pageType === 'search') {
      this.#dispatchSearchUpdateEvent(renderPromise);
    } else {
      this.#dispatchCollectionUpdateEvent(renderPromise);
    }
  }

  /**
   * Dispatches collection:update event if on a collection page.
   * @param {Promise<void>} renderPromise - The promise from section rendering
   */
  #dispatchCollectionUpdateEvent(renderPromise) {
    // Build collection object with available identifiers
    // Only use numeric IDs — non-numeric values like "all" fail GID validation
    /** @type {any} */
    const rawId = this.dataset.collectionId;
    const collectionData = {
      id: rawId && /^\d+$/.test(rawId) ? rawId : null,
      handle: /** @type {string} */ (this.dataset.collectionHandle),
      productsCount: this.#getResultsCount(),
    };

    const deferredPromise = CollectionUpdateEvent.createPromise();
    const productFilters = CollectionUpdateEvent.parseProductFilters();

    this.dispatchEvent(
      new CollectionUpdateEvent({
        collection: collectionData,
        ...(productFilters && { productFilters }),
        sortKey: CollectionUpdateEvent.getSortKey(),
        promise: deferredPromise.promise,
      })
    );

    // Resolve promise after section renders with product count
    renderPromise
      .then(() => deferredPromise.resolve({ productsCount: this.#getResultsCount() }))
      .catch((error) => deferredPromise.reject(error));
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  /** @type {Map<string, HTMLInputElement[]>} */
  #sizeGroups = new Map();

  /** @type {MutationObserver | undefined} */
  #sizeGroupObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#setupSizeGroups();

    const accordion = this.closest('accordion-custom');
    if (accordion?.dataset.filterParamName === 'filter-v-option-size') {
      this.#sizeGroupObserver = new MutationObserver(() => this.#setupSizeGroups());
      this.#sizeGroupObserver.observe(this, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#sizeGroupObserver?.disconnect();
  }

  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    this.#syncSizeGroups();
    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Updates only the drawer UI. Products are fetched after the Apply action.
   */
  updatePendingState() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    this.#syncSizeGroups();
    this.#updateSelectedFacetSummary();
    facetsForm.updateApplyButton();
  }

  /**
   * Collapses raw values such as `L` and `size L` into one visual Size option.
   * Alias inputs stay in the form so the unified option still filters both values.
   */
  #setupSizeGroups() {
    const accordion = this.closest('accordion-custom');
    if (accordion?.dataset.filterParamName !== 'filter-v-option-size') return;

    const inputs = Array.from(this.querySelectorAll('input[type="checkbox"][name="filter.v.option.size"]')).filter(
      (input) => input instanceof HTMLInputElement
    );

    this.#sizeGroups.clear();

    for (const input of inputs) {
      input.removeAttribute('data-size-group');
      input.removeAttribute('data-size-group-primary');
      input.removeAttribute('data-size-alias');

      const item = input.closest('.facets__inputs-list-item');
      if (item instanceof HTMLElement && item.dataset.sizeAliasItem === 'true') {
        item.removeAttribute('data-size-alias-item');
        item.hidden = false;
      }
    }

    for (const input of inputs) {
      const normalizedLabel = (input.dataset.label || input.value)
        .replace(/^size\s+/i, '')
        .trim()
        .toUpperCase();
      const group = this.#sizeGroups.get(normalizedLabel) || [];
      group.push(input);
      this.#sizeGroups.set(normalizedLabel, group);
    }

    for (const [groupName, group] of this.#sizeGroups) {
      const primaryInput = group.find((input) => !input.disabled) || group[0];
      const isChecked = group.some((input) => input.checked);

      for (const input of group) {
        input.dataset.sizeGroup = groupName;

        if (input === primaryInput) {
          input.dataset.sizeGroupPrimary = 'true';
          input.checked = isChecked;
          continue;
        }

        input.dataset.sizeAlias = 'true';
        const item = input.closest('.facets__inputs-list-item');
        if (item instanceof HTMLElement) {
          item.dataset.sizeAliasItem = 'true';
          item.hidden = true;
        }
      }
    }

    this.#syncSizeGroups();
    this.#updateSelectedFacetSummary();
  }

  #syncSizeGroups() {
    for (const group of this.#sizeGroups.values()) {
      const primaryInput = group.find((input) => input.dataset.sizeGroupPrimary === 'true');
      if (!primaryInput) continue;

      for (const input of group) {
        if (input === primaryInput) continue;
        input.checked = primaryInput.checked && !input.disabled;
      }
    }
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      if (this.dataset.deferApply === 'true') {
        this.updatePendingState();
      } else {
        this.updateFilters();
      }
    }
  }

  /**
   * Fires immediately on pointerdown so a quick tap triggers the prefetch.
   * @param {PointerEvent} event
   */
  prefetchPageImmediate(event) {
    if (!(event.target instanceof HTMLElement)) return;
    this.#prefetchOption(event.target);
  }

  /**
   * Reads `checked` at call time, so callers must invoke it before the toggle
   * for the predicted post-toggle URL to be correct.
   * @param {HTMLElement} optionElement
   */
  #prefetchOption(optionElement) {
    const form = this.closest('form');
    if (!form) return;

    const inputElement = optionElement.querySelector('input');
    if (!(inputElement instanceof HTMLInputElement) || inputElement.disabled) return;
    if (inputElement.dataset.sizeGroup) return;

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const formData = new FormData(form);
    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const urlParameters = facetsForm.createURLParameters(formData);
    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }

  /**
   * @param {PointerEvent} event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;
    this.#prefetchOption(event.target);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked && !input.dataset.sizeAlias);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    // Size inputs can connect before the status custom element class is defined.
    if (!statusComponent || typeof statusComponent.updateListSummary !== 'function') return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 * @property {HTMLInputElement | undefined} rangeMin - The minimum range input
 * @property {HTMLInputElement | undefined} rangeMax - The maximum range input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  /** @type {string} */
  currency;
  /** @type {string} */
  moneyFormat;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.currency = this.dataset.currency ?? 'USD';
    this.moneyFormat = this.#extractMoneyPlaceholder(this.dataset.moneyFormat ?? '{{amount}}');
    this.#updateRangeTrack();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Extracts the placeholder from a money format string, removing currency symbols.
   * @param {string} format - The money format (e.g., "${{amount}}", "{{amount}} USD")
   * @returns {string} Just the placeholder (e.g., "{{amount}}")
   */
  #extractMoneyPlaceholder(format) {
    const match = format.match(/{{\s*\w+\s*}}/);
    return match ? match[0] : '{{amount}}';
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
    this.#syncRangeInputs();
  }

  /**
   * Updates staged price values without fetching the collection.
   */
  updatePendingPriceState() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);
    this.#setMinAndMaxValues();
    this.#updateSummary();
    this.#syncRangeInputs();

    const facetsForm = this.closest('facets-form-component');
    if (facetsForm instanceof FacetsFormComponent) facetsForm.updateApplyButton();
  }

  /**
   * Mirrors the drawer range controls into Shopify's price text inputs.
   * @param {Event} event - The range input event
   */
  updateRangeInputs(event) {
    const { minInput, maxInput, rangeMin, rangeMax } = this.refs;
    if (!(rangeMin instanceof HTMLInputElement) || !(rangeMax instanceof HTMLInputElement)) return;
    if (event.target !== rangeMin && event.target !== rangeMax) return;

    if (Number(rangeMin.value) > Number(rangeMax.value)) {
      if (event.target === rangeMin) rangeMin.value = rangeMax.value;
      else rangeMax.value = rangeMin.value;
    }

    const rangeMaximum = Number(rangeMax.max || 0);
    const displayMaximum = this.#parseDisplayValue(maxInput.dataset.defaultValue ?? '', this.currency);
    const minDisplayValue = rangeMaximum > 0 ? Math.round(displayMaximum * (Number(rangeMin.value) / rangeMaximum)) : 0;
    const maxDisplayValue = rangeMaximum > 0 ? Math.round(displayMaximum * (Number(rangeMax.value) / rangeMaximum)) : 0;

    minInput.value = formatMoney(minDisplayValue, this.moneyFormat, this.currency);
    maxInput.value = formatMoney(maxDisplayValue, this.moneyFormat, this.currency);
    this.#setMinAndMaxValues();
    this.#updateSummary();
    this.#updateRangeTrack();

    const facetsForm = this.closest('facets-form-component');
    if (facetsForm instanceof FacetsFormComponent) facetsForm.updateApplyButton();
  }

  /**
   * Parses a formatted money value into minor units
   * displayValue can come from user input or API response
   * @param {string} displayValue - The display value (e.g., "10.50" for USD, "9,50" for EUR, "1000" for JPY)
   * @param {string} currency - The currency code
   * @returns {number} The value in minor units
   */
  #parseDisplayValue(displayValue, currency) {
    return convertMoneyToMinorUnits(displayValue, currency) ?? 0;
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.readOnly) {
      input.value = input.dataset.defaultValue ?? '0';
      return;
    }

    if (input.value.trim() === '') return;

    const { currency, moneyFormat } = this;
    // Parse the user's input value using currency-aware parsing
    const value = this.#parseDisplayValue(input.value, currency);

    // data-min and data-max now contain raw minor unit values (not formatted)
    const min = this.#parseDisplayValue(input.getAttribute('data-min') ?? '0', currency);
    const max = this.#parseDisplayValue(input.getAttribute('data-max') ?? '0', currency);

    if (value < min) {
      input.value = formatMoney(min, moneyFormat, currency);
    } else if (value > max) {
      input.value = formatMoney(max, moneyFormat, currency);
    }
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Mirrors text field changes back to the optional drawer range controls.
   */
  #syncRangeInputs() {
    const { minInput, maxInput, rangeMin, rangeMax } = this.refs;
    if (!(rangeMin instanceof HTMLInputElement) || !(rangeMax instanceof HTMLInputElement)) return;

    const rangeMaximum = Number(rangeMax.max || 0);
    const displayMaximum = this.#parseDisplayValue(maxInput.dataset.defaultValue ?? '', this.currency);
    const minDisplayValue = minInput.value ? this.#parseDisplayValue(minInput.value, this.currency) : 0;
    const maxDisplayValue = maxInput.value
      ? this.#parseDisplayValue(maxInput.value, this.currency)
      : displayMaximum;
    const minPosition = displayMaximum > 0 ? Math.min(1, Math.max(0, minDisplayValue / displayMaximum)) : 0;
    const maxPosition = displayMaximum > 0 ? Math.min(1, Math.max(0, maxDisplayValue / displayMaximum)) : 1;

    rangeMin.value = String(Math.round(rangeMaximum * Math.min(minPosition, maxPosition)));
    rangeMax.value = String(Math.round(rangeMaximum * Math.max(minPosition, maxPosition)));
    this.#updateRangeTrack();
  }

  /**
   * Paints the selected part of the max-price range like the Figma control.
   */
  #updateRangeTrack() {
    const { rangeMin, rangeMax } = this.refs;
    if (!(rangeMin instanceof HTMLInputElement) || !(rangeMax instanceof HTMLInputElement)) return;

    const maximum = Number(rangeMax.max || 0);
    const minPosition = maximum > 0 ? Math.min(100, Math.max(0, (Number(rangeMin.value) / maximum) * 100)) : 0;
    const maxPosition = maximum > 0 ? Math.min(100, Math.max(0, (Number(rangeMax.value) / maximum) * 100)) : 100;
    this.style.setProperty('--price-range-min-position', `${minPosition}%`);
    this.style.setProperty('--price-range-max-position', `${maxPosition}%`);
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {SearchUpdateEvent | CollectionUpdateEvent} event
   */
  #handleFilterUpdate = (/** @type {SearchUpdateEvent | CollectionUpdateEvent} */ event) => {
    // Ignore events from other sections (e.g. predictive search in header)
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('.shopify-section');
    const mySection = this.closest('.shopify-section');
    if (eventSection && mySection && eventSection !== mySection) return;

    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      const filters =
        event instanceof SearchUpdateEvent ? event.search?.productFilters ?? [] : event.productFilters ?? [];
      clearButton.classList.toggle('facets__clear--active', filters.length > 0);
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {SearchUpdateEvent | CollectionUpdateEvent} event
   */
  #handleFilterUpdate = (/** @type {SearchUpdateEvent | CollectionUpdateEvent} */ event) => {
    // Ignore events from other sections (e.g. predictive search in header)
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('.shopify-section');
    const mySection = this.closest('.shopify-section');
    if (eventSection && mySection && eventSection !== mySection) return;

    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      const activeClass = this.getAttribute('active-class') || 'active';
      const filters =
        event instanceof SearchUpdateEvent ? event.search?.productFilters ?? [] : event.productFilters ?? [];
      clearButton.classList.toggle(activeClass, filters.length > 0);
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    const target = event.target;
    let optionName = '';

    if (target instanceof HTMLSelectElement) {
      optionName = target.selectedOptions[0]?.textContent?.trim() ?? '';
    } else if (target instanceof HTMLInputElement && target.matches('input[type="radio"][name="sort_by"]')) {
      optionName = target.dataset.optionName ?? '';
    } else {
      return;
    }

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('.facets__status');
    if (!(facetStatus instanceof HTMLElement)) return;

    const shouldAlwaysShowStatus = this.classList.contains('sorting-filter--insane');
    facetStatus.textContent = shouldAlwaysShowStatus || target.value !== details.dataset.defaultSortBy ? optionName : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        const span = document.createElement('span');
        span.className = 'visually-hidden';
        span.textContent = inputElement.getAttribute('aria-label') ?? '';
        return (swatch?.outerHTML ?? '') + span.outerHTML;
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.textContent = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const currency = facetStatus.dataset.currency || '';
    const minInputNum = this.#parseCents(minInputValue, '0', currency);
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax, currency);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as minor units (cents for most currencies, but adjusted for zero-decimal currencies)
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid (formatted string like "11,400")
   * @param {string} currency - The currency code (e.g., 'USD', 'JPY', 'KRW')
   * @returns {number} The money value in minor units
   */
  #parseCents(value, fallback = '0', currency = '') {
    // Try to parse the value
    const result = convertMoneyToMinorUnits(value, currency);
    if (result !== null) return result;

    // Fall back to parsing the fallback string (which may have formatting like "11,400")
    const fallbackResult = convertMoneyToMinorUnits(fallback, currency);
    if (fallbackResult !== null) return fallbackResult;

    // Last resort: clean and parse as integer
    const cleanFallback = fallback.replace(/[^\d]/g, '');
    return parseInt(cleanFallback, 10) || 0;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const format = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return formatMoney(moneyValue, format, currency);
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}
